import env from "@/env";
import useRouter from "@@/composables/route/useRouter";
import useQueryParams from "@@/composables/useQueryParams.js";
import useLoading from "@@/composables/useLoading.js";
import { repositoryClient } from "@@/repositories/ClientAxios";
import { useLocaleStore } from "@@/stores/useLocaleStore";

export default () => {
	const commaReplacement = "|||";

	const commaReplace = (str) => {
		return String(str).replace(/,/g, commaReplacement);
	};

	const baseURL = () => {
		return `${env.API_URL}/${useLocaleStore().getLocale}`;
	};

	const queryParamsCommaReplace = (params) => {
		if (!params) return params;

		const result = {};

		Object.keys(params).forEach((key) => {
			if (Array.isArray(params[key])) {
				result[key] = params[key].map((el) => commaReplace(el));
			} else {
				result[key] = commaReplace(params[key]);
			}
		});

		return result;
	};

	const prepareUrl = (url, withQuery = false, useGlobalFilters = false, queryParamsPrefixName = "", removeQueryParamsPrefixName = true) => {
		let theUrl = url;

		if (!theUrl && !withQuery) {
			return theUrl;
		}

		if (theUrl.indexOf("http://") === -1 && theUrl.indexOf("https://") === -1) {
			theUrl = `${baseURL()}/${theUrl}`;
		}

		const queryStringInUrl = new URL(theUrl).search;

		if (queryStringInUrl) {
			const queryStringInUrlParams = Object.fromEntries(new URLSearchParams(queryStringInUrl));

			theUrl = theUrl.replace(queryStringInUrl, "");

			const queryUrl = new URLSearchParams(queryParamsCommaReplace(queryStringInUrlParams)).toString();

			if (queryUrl) theUrl += `?${queryUrl}`;
		}

		if (withQuery) {
			const { filterQuery } = useRouter();

			const routeQueryParams = filterQuery(new RegExp(`^${queryParamsPrefixName}`), removeQueryParamsPrefixName);
			const queryUrl = new URLSearchParams(queryParamsCommaReplace(routeQueryParams)).toString();

			if (queryUrl) theUrl += `${theUrl.includes("?") ? "&" : "?"}${queryUrl}`;

			if (useGlobalFilters) {
				const { getQueryParams } = useQueryParams();
				const globalParams = getQueryParams();

				if (globalParams && Object.keys(globalParams).length > 0) {
					Object.entries(globalParams).forEach(([key, value]) => {
						const separator = theUrl.includes("?") ? "&" : "?";
						theUrl += `${separator}${key}=${value}`;
					});
				}
			}
		}

		return theUrl;
	};

	const abortControllers = {};

	const getAbortController = (url) => {
		if (!url) {
			return null;
		}

		const { requestStop } = useLoading();

		if (abortControllers && abortControllers[url]) {
			abortControllers[url].abort(url);
			requestStop(url);
			delete abortControllers[url];
		}
		abortControllers[url] = new AbortController();

		return abortControllers[url];
	};

	const get = (
		url,
		params = null,
		abort = true,
		useGlobalFilters = false,
		loadingSilent = false,
		raw = false,
		queryParamsPrefixName = "",
		removeQueryParamsPrefixName = true
	) => {
		let theUrl = prepareUrl(url, !raw, useGlobalFilters, queryParamsPrefixName, removeQueryParamsPrefixName);

		let data = {};
		if (null !== params) data.params = queryParamsCommaReplace(params);

		if (true === abort) {
			const abortController = getAbortController(url);
			data.signal = abortController.signal;
		}

		if (loadingSilent) {
			const { setRequestSilent } = useLoading();
			setRequestSilent(theUrl);
		}

		return new Promise((resolve, reject) => {
			repositoryClient
				.get(theUrl, data)
				.then((response) => {
					resolve(response);
				})
				.catch((error) => {
					reject(error);
				});
		});
	};

	const getRaw = (
		url,
		params = null,
		abort = true,
		useGlobalFilters = false,
		loadingSilent = false,
		queryParamsPrefixName = "",
		removeQueryParamsPrefixName = true
	) => {
		return get(url, params, abort, useGlobalFilters, loadingSilent, true, queryParamsPrefixName, removeQueryParamsPrefixName);
	};

	const post = (
		url,
		data = {},
		abort = false,
		postConfig = {},
		loadingSilent = false,
		withQuery = false,
		useGlobalFilters = false,
		queryParamsPrefixName = "",
		removeQueryParamsPrefixName = true
	) => {
		url = prepareUrl(url, withQuery, useGlobalFilters, queryParamsPrefixName, removeQueryParamsPrefixName);

		if (true === abort) {
			const abortController = getAbortController(url);
			data.signal = abortController.signal;
		}

		if (loadingSilent) {
			const { setRequestSilent } = useLoading();
			setRequestSilent(url);
		}

		return new Promise((resolve, reject) => {
			repositoryClient
				.post(`${url}`, data, postConfig)
				.then((response) => {
					resolve(response);
				})
				.catch((error) => {
					reject(error);
				});
		});
	};

	const postWithQuery = (
		url,
		data = {},
		abort = false,
		postConfig = {},
		loadingSilent = false,
		useGlobalFilters = false,
		queryParamsPrefixName = "",
		removeQueryParamsPrefixName = true
	) => {
		return post(url, data, abort, postConfig, loadingSilent, true, useGlobalFilters, queryParamsPrefixName, removeQueryParamsPrefixName);
	};

	const put = (url, data = {}, loadingSilent = false) => {
		url = prepareUrl(url);

		if (loadingSilent) {
			const { setRequestSilent } = useLoading();
			setRequestSilent(url);
		}

		return new Promise((resolve, reject) => {
			repositoryClient
				.put(`${url}`, data)
				.then((response) => {
					resolve(response);
				})
				.catch((error) => {
					reject(error);
				});
		});
	};

	const del = (url, data = {}, loadingSilent = false) => {
		url = prepareUrl(url);

		if (loadingSilent) {
			const { setRequestSilent } = useLoading();
			setRequestSilent(url);
		}

		return new Promise((resolve, reject) => {
			repositoryClient
				.delete(`${url}`, {
					data: data,
				})
				.then((response) => {
					resolve(response);
				})
				.catch((error) => {
					reject(error);
				});
		});
	};

	const getOne = (url, id) => {
		url += id ? "/" + id : "";
		return getRaw(url);
	};

	const create = (url, data) => {
		return post(`${url}`, data);
	};

	const update = (url, id, data) => {
		url += id ? "/" + id : "";
		return put(`${url}`, data);
	};

	const deleteOne = (url, id) => {
		url += id ? "/" + id : "";
		return del(`${url}`);
	};

	return {
		baseURL,
		get,
		getRaw,
		post,
		postWithQuery,
		put,
		del,
		getOne,
		create,
		update,
		deleteOne,
	};
};
